import { useEffect, useState, type ReactNode } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import type { Customer, CustomerInput } from '@iobiz/shared'
import {
  aUnPrefixe,
  prefixePays,
  saisieTelephone,
  versE164, customerInputSchema, uuidv7 } from '@iobiz/shared'
import { ChoixPortrait } from '../../ui/ChoixPortrait.js'
import { useSessionStore } from '../../stores/sessionStore.js'
import { useEtiquettes } from '../../data/hooks.js'
import { ChampDateNaissance } from '../../ui/ChampDateNaissance.js'
import { saveCustomer } from '../../data/repositories/customers.write.js'

/**
 * Création et édition d'un client.
 *
 * Le MÊME schéma Zod valide ici et côté serveur (ADR-14) : une règle écrite
 * deux fois finit toujours par diverger. La validation locale sert le confort
 * — retour immédiat, pas d'aller-retour — mais ne remplace jamais celle du
 * serveur, qui reste la seule à faire autorité.
 */

interface Props {
  open: boolean
  storeId: string
  /** Client à modifier ; absent = création. */
  customer?: Customer | null
  /** Nom pré-rempli, quand la création part de la palette de commandes. */
  initialName?: string
  onClose: () => void
  onSaved: (customer: Customer) => void
}

/**
 * Un groupe de champs sous un intertitre.
 *
 * Onze champs alignés d'affilée se lisent comme une liste indifférenciée : on
 * cherche « où est la ville ? » au lieu de la voir. Le découpage reprend celui
 * de la fiche client (Identité / Contact / Adresse / Commercial), pour que le
 * formulaire et la fiche se parcourent dans le même ordre.
 */
function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Box component="section" sx={{ mt: 5, '&:first-of-type': { mt: 0 } }}>
      <Typography
        component="h3"
        variant="overline"
        sx={{ display: 'block', color: 'text.secondary', letterSpacing: '.08em', mb: 2 }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {children}
      </Box>
    </Box>
  )
}

/**
 * Référence client, ENGENDRÉE par le système.
 *
 * Ce n'est pas une donnée que l'utilisateur saisit : c'est l'identifiant sous
 * lequel la boutique désigne le client. Côté client (et non serveur) pour la
 * même raison que pour les articles — une fiche doit pouvoir naître hors ligne
 * en portant déjà sa référence.
 */
function genererReference(): string {
  // ⚠️ La FIN de l'identifiant, jamais le début : un UUIDv7 commence par un
  // horodatage à la milliseconde, et deux fiches créées coup sur coup
  // recevaient la même référence — que l'index d'unicité refuse ensuite.
  const suffixe = uuidv7().replace(/-/g, '').slice(-10)
  return `CLI-${parseInt(suffixe, 16).toString(36).toUpperCase().slice(-6)}`
}

type FormState = Record<string, string>

const EMPTY: FormState = {
  type: 'individual',
  firstName: '',
  lastName: '',
  companyName: '',
  code: '',
  taxId: '',
  birthDate: '',
  email: '',
  phone: '',
  mobile: '',
  contactFirstName: '',
  contactLastName: '',
  contactRole: '',
  contactPhone: '',
  contactEmail: '',
  addressLine1: '',
  postalCode: '',
  city: '',
  countryCode: 'CM',
  paymentTermsDays: '0',
  notes: '',
}

function fromCustomer(c: Customer): FormState {
  return {
    type: c.type,
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    companyName: c.companyName ?? '',
    code: c.code ?? '',
    taxId: c.taxId ?? '',
    birthDate: c.birthDate ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    mobile: c.mobile ?? '',
    contactFirstName: c.contactFirstName ?? '',
    contactLastName: c.contactLastName ?? '',
    contactRole: c.contactRole ?? '',
    contactPhone: c.contactPhone ?? '',
    contactEmail: c.contactEmail ?? '',
    addressLine1: c.addressLine1 ?? '',
    postalCode: c.postalCode ?? '',
    city: c.city ?? '',
    countryCode: c.countryCode ?? 'CM',
    paymentTermsDays: String(c.paymentTermsDays),
    notes: c.notes ?? '',
  }
}

export function CustomerFormDialog({
  open,
  storeId,
  customer,
  initialName,
  onClose,
  onSaved,
}: Props): JSX.Element {
  const editing = Boolean(customer)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [avatar, setAvatar] = useState<string | null>(null)
  const [tags, setTags] = useState<string[]>([])
  /**
   * L'étiquette EN COURS DE FRAPPE, pas encore validée par Entrée.
   *
   * ⚠️ Sans elle, une étiquette tapée puis enregistrée directement était
   * perdue en silence : `Autocomplete` en saisie libre ne la range dans sa
   * valeur qu'à la validation, et « Enregistrer » partait avec la liste
   * d'avant. L'utilisateur voyait son « VIP » à l'écran, cliquait, et la
   * fiche revenait sans rien — le cas a été rapporté tel quel.
   */
  const [tagEnCours, setTagEnCours] = useState('')
  // Ce qui existe déjà dans la boutique : on propose avant de laisser taper.
  const etiquettesConnues = useEtiquettes(storeId)
  /**
   * ⭐ L'indicatif de la BOUTIQUE, pré-inscrit dans les champs téléphone. Il
   * reste modifiable : une touriste, une cliente expatriée ou un fournisseur
   * étranger n'ont pas le même. C'est un point de départ, pas une contrainte.
   */
  const paysBoutique = useSessionStore(
    (s) => s.stores.find((b) => b.storeId === storeId)?.countryCode ?? null,
  )
  const prefixe = prefixePays(paysBoutique)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [banner, setBanner] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setErrors({})
    setBanner(null)
    setAvatar(customer?.avatarUrl ?? null)
    setTags(customer?.tags ?? [])
    setTagEnCours('')
    if (customer) {
      /**
       * ⭐ Les numéros d'une fiche ancienne sont REMONTÉS au format
       * international à l'ouverture — dans le formulaire seulement, tant qu'on
       * n'enregistre pas. Sans cela, ouvrir une fiche d'avant cette règle pour
       * y corriger une virgule se serait soldé par un refus sur un champ qu'on
       * n'avait pas touché.
       *
       * ⚠️ Le pays de la FICHE d'abord : une cliente camerounaise d'une
       * boutique malienne garde son +237.
       */
      const paysFiche = customer.countryCode ?? paysBoutique
      const monter = (v: string): string => (v ? (versE164(v, paysFiche) ?? v) : v)
      const base = fromCustomer(customer)
      setForm({
        ...base,
        phone: monter(base['phone'] ?? ''),
        mobile: monter(base['mobile'] ?? ''),
        contactPhone: monter(base['contactPhone'] ?? ''),
      })
    }
    // Depuis la palette : le texte tapé devient le nom, il n'y a rien à ressaisir.
    else
      setForm({
        ...EMPTY,
        lastName: initialName ?? '',
        code: genererReference(),
        // Pré-saisie : on tape le numéro à la suite de l'indicatif.
        phone: prefixe ?? '',
        mobile: prefixe ?? '',
        contactPhone: prefixe ?? '',
      })
  }, [open, customer, initialName, prefixe, paysBoutique])

  function set(field: string, value: string): void {
    setForm((f) => ({ ...f, [field]: value }))
    if (errors[field]) setErrors(({ [field]: _removed, ...rest }) => rest)
  }

  async function submit(): Promise<void> {
    /**
     * ⚠️ L'interlocuteur est EFFACÉ dès que la fiche n'est plus une société.
     *
     * Ne plus afficher un champ ne l'efface pas : sa valeur vit dans l'état du
     * formulaire, pas dans le DOM. Une fiche société renseignée puis basculée
     * en particulier serait donc partie avec le nom, le téléphone et l'adresse
     * d'un interlocuteur que plus aucun écran ne montre — invisible à la
     * relecture, mais bien présent en base, dans les exports et dans la
     * recherche.
     */
    const interlocuteur = isCompany
      ? {}
      : {
          contactFirstName: '',
          contactLastName: '',
          contactRole: '',
          contactPhone: '',
          contactEmail: '',
        }

    /**
     * ⚠️ BLOCAGE SANS INDICATIF. Un numéro national ne veut rien dire hors de
     * son pays : il ne se compose pas depuis l'étranger, ne correspond à rien
     * pour WhatsApp, et arrive faux dans le carnet d'adresses du téléphone.
     * Le champ étant pré-rempli de l'indicatif, le refus ne tombe que sur une
     * saisie où on l'a délibérément effacé.
     *
     * ⭐ L'indicatif SEUL vaut « pas de numéro » : sans cette lecture, toute
     * fiche créée sans téléphone porterait « +223 » comme numéro.
     */
    const tels = {
      phone: saisieTelephone(form['phone'] ?? '', prefixe),
      mobile: saisieTelephone(form['mobile'] ?? '', prefixe),
      contactPhone: saisieTelephone(form['contactPhone'] ?? '', prefixe),
    }
    const manquants: Record<string, string> = {}
    for (const [champ, valeur] of Object.entries(tels)) {
      if (champ === 'contactPhone' && !isCompany) continue
      if (valeur && !aUnPrefixe(valeur)) {
        manquants[champ] =
          `Indicatif international requis${prefixe ? ` — par exemple ${prefixe} 76 12 34 56` : ''}.`
      }
    }
    if (Object.keys(manquants).length > 0) {
      setErrors(manquants)
      return
    }

    /**
     * Les étiquettes retenues : celles déjà validées, plus celle qui est
     * encore dans le champ. Dédoublonnées et plafonnées comme à la saisie.
     */
    function etiquettesAEnregistrer(): string[] {
      const enCours = tagEnCours.trim()
      const toutes = enCours ? [...tags, enCours] : tags
      return [...new Set(toutes)].slice(0, 20)
    }

    const parsed = customerInputSchema.safeParse({
      ...form,
      phone: tels.phone ?? '',
      mobile: tels.mobile ?? '',
      contactPhone: tels.contactPhone ?? '',
      ...interlocuteur,
      paymentTermsDays: Number(form['paymentTermsDays'] || 0),
      avatarUrl: avatar ?? undefined,
      /**
       * ⚠️ Était `tags: []` EN DUR. La colonne « Étiquettes » de la liste
       * existait depuis le premier sprint et n'a jamais rien pu afficher :
       * modifier une fiche effaçait même les étiquettes posées par ailleurs.
       */
      // ⭐ On ramasse la frappe en cours : ce que l'utilisateur VOIT dans le
      // champ fait partie de ce qu'il croit enregistrer.
      tags: etiquettesAEnregistrer(),
    })

    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.') || 'form'
        fieldErrors[key] ??= issue.message
      }
      setErrors(fieldErrors)
      return
    }

    setSaving(true)
    setBanner(null)
    try {
      // ⭐ Écriture LOCALE puis mise en file : on n'attend pas le réseau. Hors
      // ligne, l'enregistrement aboutit exactement de la même façon, et la
      // mutation partira au retour de la connexion.
      const saved = await saveCustomer(storeId, parsed.data as CustomerInput, customer)
      onSaved(saved)
      onClose()
    } catch (err) {
      // Il ne reste ici que les défaillances du stockage local — quota dépassé,
      // navigation privée. Le réseau, lui, n'est plus dans la boucle.
      setBanner(
        err instanceof Error
          ? `Enregistrement local impossible : ${err.message}`
          : 'Enregistrement local impossible.',
      )
    } finally {
      setSaving(false)
    }
  }

  const isCompany = form['type'] === 'company'

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>

      <DialogContent dividers>
        {banner && (
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {banner}
          </Alert>
        )}

        <Section title="Identité">
          {/* ⭐ Tout tient SUR LA HAUTEUR DE LA PHOTO : type et référence sur une
              ligne, identité sur la suivante. La pastille est en mode compact —
              ses boutons prendraient plus de place qu'elle, et le glisser-déposer
              reste actif sur la pastille comme sur le bloc. 112 px de photo pour
              deux rangées de 32 et 56, séparées de 16 : le compte tombe juste. */}
          <Box
            sx={{
              gridColumn: '1 / -1',
              display: 'flex',
              gap: 4,
              alignItems: 'flex-start',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <ChoixPortrait value={avatar} onChange={setAvatar} taille={112} compact />

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'grid',
                gap: 2,
                gridTemplateRows: 'auto auto',
                alignContent: 'space-between',
                minHeight: 112,
              }}
            >
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={form['type']}
                  onChange={(_, v: string | null) => v && set('type', v)}
                >
                  <ToggleButton value="individual">Particulier</ToggleButton>
                  <ToggleButton value="company">Société</ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ flex: 1 }} />

                {/* Engendrée par le système : on la montre, on ne la saisit pas. */}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Référence
                </Typography>
                <Typography variant="body2" className="tabular" sx={{ fontWeight: 600 }}>
                  {form['code'] || '—'}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: { xs: '1fr', sm: isCompany ? '1fr' : '1fr 1fr' },
                }}
              >
                {isCompany ? (
                  <TextField
                    label="Raison sociale"
                    required
                    value={form['companyName']}
                    onChange={(e) => set('companyName', e.target.value)}
                    error={Boolean(errors['companyName'])}
                    helperText={errors['companyName']}
                  />
                ) : (
                  <>
                    <TextField
                      label="Nom"
                      required
                      value={form['lastName']}
                      onChange={(e) => set('lastName', e.target.value)}
                      error={Boolean(errors['lastName'])}
                      helperText={errors['lastName']}
                    />
                    <TextField
                      label="Prénom"
                      value={form['firstName']}
                      onChange={(e) => set('firstName', e.target.value)}
                    />
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* ⭐ Une date de naissance ne concerne qu'une PERSONNE : une société
              n'en a pas. Le champ suit donc le type de fiche, comme le n°
              fiscal suit l'autre. Il descend hors du bloc d'entête, qui doit
              rester à la hauteur de la photo. */}
          {!isCompany && (
            <ChampDateNaissance
              value={form['birthDate'] ?? ''}
              onChange={(v) => set('birthDate', v)}
              error={Boolean(errors['birthDate'])}
              {...(errors['birthDate'] ? { helperText: errors['birthDate'] } : {})}
            />
          )}

          {/* Le n° fiscal ne concerne que les sociétés : il descend hors du bloc
              d'entête, qui doit rester à la hauteur de la photo. */}
          {isCompany && (
            <TextField
              label="N° fiscal"
              value={form['taxId']}
              onChange={(e) => set('taxId', e.target.value)}
              sx={{ gridColumn: '1 / -1' }}
            />
          )}
        </Section>

        <Section title="Contact">
          <TextField
            label="Téléphone"
            value={form['phone']}
            onChange={(e) => set('phone', e.target.value)}
            error={Boolean(errors['phone'])}
            helperText={
              errors['phone'] ?? (prefixe ? `Indicatif ${prefixe} par défaut, modifiable` : undefined)
            }
          />
          <TextField
            label="Mobile"
            value={form['mobile']}
            onChange={(e) => set('mobile', e.target.value)}
            error={Boolean(errors['mobile'])}
            helperText={errors['mobile'] ?? (prefixe ? `Indicatif ${prefixe} par défaut` : undefined)}
          />
          <TextField
            label="Email"
            type="email"
            value={form['email']}
            onChange={(e) => set('email', e.target.value)}
            error={Boolean(errors['email'])}
            helperText={errors['email']}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Section>

{/* ⭐ Réservé aux SOCIÉTÉS : un particulier est son propre interlocuteur,
            lui demander « qui contacter » sur sa propre fiche n'aurait pas de
            sens et allongerait un formulaire déjà long. Le nettoyage de ces
            champs quand on repasse en particulier se fait à l'envoi, PAS ici —
            voir « submit ». */}
        {isCompany && (
          <Section title="Personne à contacter">
            <TextField
              label="Nom"
              value={form['contactLastName']}
              onChange={(e) => set('contactLastName', e.target.value)}
            />
            <TextField
              label="Prénom"
              value={form['contactFirstName']}
              onChange={(e) => set('contactFirstName', e.target.value)}
            />
            <TextField
              label="Fonction"
              placeholder="Directrice des achats"
              value={form['contactRole']}
              onChange={(e) => set('contactRole', e.target.value)}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label="Téléphone"
              value={form['contactPhone']}
              onChange={(e) => set('contactPhone', e.target.value)}
              error={Boolean(errors['contactPhone'])}
              helperText={errors['contactPhone']}
            />
            <TextField
              label="Email"
              type="email"
              value={form['contactEmail']}
              onChange={(e) => set('contactEmail', e.target.value)}
              error={Boolean(errors['contactEmail'])}
              helperText={errors['contactEmail']}
            />
          </Section>
        )}

        <Section title="Adresse">
          <TextField
            label="Adresse"
            value={form['addressLine1']}
            onChange={(e) => set('addressLine1', e.target.value)}
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            label="Code postal"
            value={form['postalCode']}
            onChange={(e) => set('postalCode', e.target.value)}
          />
          <TextField
            label="Ville"
            value={form['city']}
            onChange={(e) => set('city', e.target.value)}
          />
        </Section>

        <Section title="Commercial">
          <TextField
            select
            label="Conditions de paiement"
            value={form['paymentTermsDays']}
            onChange={(e) => set('paymentTermsDays', e.target.value)}
          >
            <MenuItem value="0">Comptant</MenuItem>
            <MenuItem value="15">15 jours</MenuItem>
            <MenuItem value="30">30 jours</MenuItem>
            <MenuItem value="45">45 jours</MenuItem>
            <MenuItem value="60">60 jours</MenuItem>
          </TextField>

          {/**
            * ⭐ Les ÉTIQUETTES, enfin saisissables.
            *
            * Ce sont des marques libres qu'on pose sur une fiche pour la
            * retrouver en groupe — « Fidèle », « Grossiste », « Relance ». La
            * liste des clients les affiche et sait filtrer dessus ; il ne
            * manquait que de quoi en poser une.
            *
            * ⚠️ `freeSolo` avec les étiquettes DÉJÀ EMPLOYÉES en suggestion :
            * en saisie purement libre, « Fidèle », « fidele » et « Fidéle »
            * cohabitent au bout d'un mois et le filtre ne ramène plus qu'une
            * fiche sur trois.
            */}
          <Autocomplete
            multiple
            freeSolo
            options={etiquettesConnues}
            value={tags}
            inputValue={tagEnCours}
            onInputChange={(_, v) => setTagEnCours(v)}
            onChange={(_, v) => {
              // Doublons et espaces retirés : une même étiquette deux fois sur
              // une fiche n'a pas de sens, et « Fidèle » ne doit pas différer
              // de « Fidèle ».
              const propres = v.map((t) => t.trim()).filter(Boolean)
              setTags([...new Set(propres)].slice(0, 20))
              setTagEnCours('')
            }}
            renderTags={(valeurs, getProps) =>
              valeurs.map((t, i) => {
                const { key, ...props } = getProps({ index: i })
                return <Chip key={key} label={t} size="small" {...props} />
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Étiquettes"
                placeholder="Fidèle, Grossiste…"
                helperText="Entrée pour valider. Sert à filtrer la liste des clients."
              />
            )}
            sx={{ gridColumn: '1 / -1' }}
          />

          <TextField
            label="Notes"
            multiline
            minRows={2}
            value={form['notes']}
            onChange={(e) => set('notes', e.target.value)}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Section>

        {editing && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 4, display: 'block' }}>
            Version {customer!.version} — si un collègue modifie cette fiche entre-temps,
            l’enregistrement sera refusé plutôt que d’écraser son travail.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={() => void submit()} disabled={saving}>
          {saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Créer le client'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
